import { Box, Checkbox, Chip, InputAdornment, Skeleton, TextField, Typography } from '@mui/material';
import Search from '@mui/icons-material/Search';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import FolderOffOutlined from '@mui/icons-material/FolderOffOutlined';
import React, { useEffect, useMemo, useState } from "react";
import { updateObjectStateValue } from "../../../utils/FunctionHelpers";
import {
    addremovecandidattoselection,
    getAllCadidatSelections
} from "../../../services/SelectionCandidatService";
import { withSnackbar } from "../../../utils/SnackbarHOC";
import { AppButton } from "../../../components/UI";
import Modal from "../../../components/Modal/Modal";

const normalize = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const toIdList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const ChooseMultipleSelectionModal = ({ children, selectedCandidats, snackbarShowMessage, reloadSelections, setReloadSelections, currentSelection }) => {
    const [openModal, setOpenModal] = useState(false);
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [search, setSearch] = useState('');
    const [isAddingSelection, setIsAddingSelection] = useState(false);
    const [selectionData, setSelectionData] = useState({ isLoading: false, data: [] });

    const currentIds = useMemo(() => new Set(toIdList(currentSelection)), [currentSelection]);

    const loadSelections = () => {
        updateObjectStateValue(setSelectionData, { isLoading: true, data: [] });
        getAllCadidatSelections('?archived=false').then(({ data }) => {
            updateObjectStateValue(setSelectionData, { isLoading: false, data: data.candidatsSelections || [] });
        }).catch(() => {
            snackbarShowMessage("Erreur de chargement des sélections.", "error", 4000);
        }).finally(() => {
            updateObjectStateValue(setSelectionData, { isLoading: false });
        });
    };

    const handleClose = () => { if (!isAddingSelection) setOpenModal(false); };
    const handleOpen = () => {
        setSearch('');
        setOpenModal(true);
    };

    const handleAddSelection = async () => {
        snackbarShowMessage("Ajout aux selections en cours...", "warning", "4000");
        setIsAddingSelection(true);
        try {
            const ids = [...checkedIds];
            for (const candidatId of selectedCandidats) {
                await addremovecandidattoselection({ selectedSelections: ids }, candidatId);
            }
            snackbarShowMessage("Ajout aux selections terminé avec succès", "success", "4000");
        } catch (e) {
            snackbarShowMessage("Erreur lors de l'ajout des sélections", "error", "4000");
        } finally {
            setOpenModal(false);
            setReloadSelections(!reloadSelections);
            setIsAddingSelection(false);
            setTimeout(() => { window.location.reload(); }, 1000);
        }
    };

    // Pre-check the selections the candidate(s) are already in.
    useEffect(() => {
        if (openModal) {
            setCheckedIds(new Set(selectionData.data.filter((s) => currentIds.has(s.id)).map((s) => s.id)));
        }
    }, [openModal, selectionData.data, currentIds]);

    useEffect(() => {
        if (openModal) loadSelections();
        return () => setSelectionData({ isLoading: false, data: [] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal]);

    const toggle = (id) => setCheckedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // Already-linked selections first, then the rest; filtered by name, mission or talent manager.
    const visible = useMemo(() => {
        const q = normalize(search.trim());
        return [...selectionData.data]
            .filter((s) => !q || [s.selectionName, s.nom_mission, s.creator].some((v) => normalize(v).includes(q)))
            .sort((a, b) => Number(currentIds.has(b.id)) - Number(currentIds.has(a.id)));
    }, [selectionData.data, search, currentIds]);

    const count = selectedCandidats?.length ?? 0;

    const body = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1' }}>
                <GroupsOutlined sx={{ color: '#03407b' }} />
                <Typography sx={{ fontSize: '0.9rem', color: '#03407b' }}>
                    <b>{count} candidat{count > 1 ? 's' : ''}</b> — cochez les sélections dans lesquelles {count > 1 ? 'ils doivent' : 'il doit'} figurer
                </Typography>
            </Box>

            <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Rechercher une sélection, une mission, un talent manager…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8' }} /></InputAdornment> } }}
            />

            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', maxHeight: 340, overflowY: 'auto' }}>
                {selectionData.isLoading ? (
                    [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={52} sx={{ m: 1, borderRadius: '8px' }} />)
                ) : visible.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, color: '#94a3b8' }}>
                        <FolderOffOutlined />
                        <Typography sx={{ fontSize: '0.88rem' }}>
                            {search ? 'Aucune sélection ne correspond à votre recherche' : 'Aucune sélection disponible'}
                        </Typography>
                    </Box>
                ) : visible.map((s) => {
                    const checked = checkedIds.has(s.id);
                    return (
                        <Box
                            key={s.id}
                            onClick={() => toggle(s.id)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9', background: checked ? '#f0f7fd' : '#fff',
                                '&:last-of-type': { borderBottom: 'none' },
                                '&:hover': { background: checked ? '#e6f1fb' : '#f8fafc' },
                            }}
                        >
                            <Checkbox size="small" checked={checked} tabIndex={-1} sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#03407b' } }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {s.selectionName || 'Sans nom'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.76rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {[s.creator, s.nom_mission].filter(Boolean).join(' · ') || '—'}
                                </Typography>
                            </Box>
                            {currentIds.has(s.id) && (
                                <Chip label="Actuelle" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }} />
                            )}
                            {s.totalCandidats != null && (
                                <Chip label={`${s.totalCandidats} cand.`} size="small" sx={{ height: 20, fontSize: '0.68rem', background: '#f1f5f9', color: '#475569' }} />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );

    const actions = (
        <>
            <Typography sx={{ mr: 'auto', fontSize: '0.82rem', color: '#64748b' }}>
                {checkedIds.size} sélection{checkedIds.size > 1 ? 's' : ''} cochée{checkedIds.size > 1 ? 's' : ''}
            </Typography>
            <AppButton variant="secondary" onClick={handleClose} disabled={isAddingSelection}>
                Annuler
            </AppButton>
            <AppButton
                variant="primary"
                disabled={selectionData.isLoading || isAddingSelection}
                loading={isAddingSelection}
                onClick={handleAddSelection}
            >
                Enregistrer
            </AppButton>
        </>
    );

    return (
        <>
            {children(handleOpen)}
            <Modal
                open={openModal}
                onClose={handleClose}
                title="Ajouter à une sélection existante"
                maxWidth="sm"
                actions={actions}
            >
                {body}
            </Modal>
        </>
    );
};

export default withSnackbar(ChooseMultipleSelectionModal);
